import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { SelectButton } from 'primereact/selectbutton';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { toast } from 'react-toastify';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { handlePutRequest } from '../../service/PutTemplate';
import { endPoints } from '../../Config';
import { getPreferredLanguage } from '../../utilities/languagePreference';
import { translateUiText } from '../../utilities/siteTranslations';

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '-');

const COLLECTION_LABELS = {
    daily_routes: 'Daily Routes',
    consolidation: 'Consolidation',
    route_sales: 'Route Sales',
    collection_stats: 'Collection Stats',
    pos_sales: 'POS Sales',
    machine_inventory: 'Machine Inventory',
    change_levels: 'Change Levels'
};

const DATA_TABS = [
    { key: 'daily_routes', label: 'Daily Routes', endpoint: 'parlevelDailyRoutes' },
    { key: 'route_sales', label: 'Route Sales', endpoint: 'parlevelRouteSales' },
    { key: 'consolidation', label: 'Consolidation', endpoint: 'parlevelConsolidation' },
    { key: 'machine_inventory', label: 'Machine Inventory', endpoint: 'parlevelMachineInventory' },
    { key: 'change_levels', label: 'Change Levels', endpoint: 'parlevelChangeLevels' },
    { key: 'pos_sales', label: 'POS Sales', endpoint: 'parlevelPosSales' },
    { key: 'collection_stats', label: 'Collection Stats', endpoint: 'parlevelCollectionStats' }
];

const ParlevelData = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [preferredLanguage, setPreferredLanguage] = useState(getPreferredLanguage());
    const appT = (value) => translateUiText(value, preferredLanguage);

    // Status
    const [status, setStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);

    // Logs
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    // Manual run
    const [runningMorning, setRunningMorning] = useState(false);
    const [runningNightly, setRunningNightly] = useState(false);
    const [runningChangeLevels, setRunningChangeLevels] = useState(false);

    // Backfill
    const [backfillMode, setBackfillMode] = useState('single'); // 'single' | 'range'
    const [backfillSingleDate, setBackfillSingleDate] = useState(null);
    const [backfillFromDate, setBackfillFromDate] = useState(null);
    const [backfillToDate, setBackfillToDate] = useState(null);
    const [backfillScrapers, setBackfillScrapers] = useState({
        consolidation: true,
        route_sales: true,
        pos_sales: true,
        collection_stats: true,
        smart_routing: true,
    });
    const [runningBackfill, setRunningBackfill] = useState(false);

    // Report email recipients / manual trigger
    const [scheduledRecipients, setScheduledRecipients] = useState({
        daily: [],
        weekly: [],
        monthly: []
    });
    const [legacyRecipients, setLegacyRecipients] = useState([]);
    const [testRecipients, setTestRecipients] = useState([]);
    const [testReportType, setTestReportType] = useState('daily');
    const [recipientOptions, setRecipientOptions] = useState([]);
    const [reportRecipientsLoading, setReportRecipientsLoading] = useState(false);
    const [savingReportRecipients, setSavingReportRecipients] = useState(false);
    const [triggeringReportType, setTriggeringReportType] = useState('');

    // Data explorer
    const [activeDataTab, setActiveDataTab] = useState(0);
    const [dataRows, setDataRows] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [dataLimit, setDataLimit] = useState(50);
    const initialLoadDone = useRef(false);

    useEffect(() => {
        const sync = () => setPreferredLanguage(getPreferredLanguage());
        window.addEventListener('preferred-language-changed', sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener('preferred-language-changed', sync);
            window.removeEventListener('storage', sync);
        };
    }, []);

    // ── Status ──────────────────────────────────────────
    const fetchStatus = useCallback(async () => {
        setStatusLoading(true);
        try {
            const res = await handleGetRequest(endPoints.parlevelStatus, false, false, dispatch);
            if (res) setStatus(res?.data || res);
        } catch {
            toast.error('Failed to load Parlevel status');
        }
        setStatusLoading(false);
    }, [dispatch]);

    // ── Logs ────────────────────────────────────────────
    const fetchLogs = useCallback(async () => {
        setLogsLoading(true);
        try {
            const res = await handleGetRequest(endPoints.parlevelScrapeLogs('limit=30'), false, false, dispatch);
            if (res) setLogs(res?.data || []);
        } catch {
            toast.error('Failed to load scrape logs');
        }
        setLogsLoading(false);
    }, [dispatch]);

    const fetchReportRecipients = useCallback(async () => {
        setReportRecipientsLoading(true);
        try {
            const res = await handleGetRequest(endPoints.parlevelReportRecipients, false, false, dispatch);
            const payload = res?.data || {};
            const options = Array.isArray(payload.user_options) ? payload.user_options : [];
            const selectedEmails = Array.isArray(payload.recipients) ? payload.recipients : [];
            const selectedByType = payload.scheduled_recipients && typeof payload.scheduled_recipients === 'object'
                ? payload.scheduled_recipients
                : {};
            setRecipientOptions(
                options.map((option) => ({
                    label: `${option.name || option.email} (${option.email})`,
                    value: option.email,
                    email: option.email,
                    name: option.name || option.email,
                    user_id: option.user_id || null
                }))
            );
            setLegacyRecipients(selectedEmails);
            setScheduledRecipients({
                daily: Array.isArray(selectedByType.daily) ? selectedByType.daily : selectedEmails,
                weekly: Array.isArray(selectedByType.weekly) ? selectedByType.weekly : selectedEmails,
                monthly: Array.isArray(selectedByType.monthly) ? selectedByType.monthly : selectedEmails
            });
            setTestRecipients([]);
        } catch {
            toast.error('Failed to load report recipients');
        }
        setReportRecipientsLoading(false);
    }, [dispatch]);

    // ── Data explorer ───────────────────────────────────
    const fetchDataTab = useCallback(async (tabIndex) => {
        const tab = DATA_TABS[tabIndex];
        if (!tab) return;
        setDataLoading(true);
        try {
            const endpointFn = endPoints[tab.endpoint];
            const res = await handleGetRequest(endpointFn(`limit=${dataLimit}`), false, false, dispatch);
            if (res) setDataRows(res?.data || []);
        } catch {
            toast.error(`Failed to load ${tab.label}`);
        }
        setDataLoading(false);
    }, [dispatch, dataLimit]);

    useEffect(() => {
        if (initialLoadDone.current) return;
        initialLoadDone.current = true;
        fetchStatus();
        fetchLogs();
        fetchReportRecipients();
    }, []);

    useEffect(() => {
        fetchDataTab(activeDataTab);
    }, [activeDataTab, fetchDataTab]);

    // ── Manual runs ─────────────────────────────────────
    const handleRunMorning = () => {
        confirmDialog({
            message: appT('This will start a morning scrape. Continue?'),
            header: appT('Confirm Morning Run'),
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                setRunningMorning(true);
                try {
                    const res = await dispatch(handlePostRequest({}, endPoints.parlevelRunMorning, true, true));
                    if (res?.status === 200 || res?.status === 201) {
                        toast.success(appT('Morning run started'));
                        setTimeout(() => { fetchStatus(); fetchLogs(); }, 3000);
                    }
                } catch {
                    toast.error(appT('Morning run failed'));
                }
                setRunningMorning(false);
            }
        });
    };

    const handleRunNightly = () => {
        confirmDialog({
            message: appT('This will start a full nightly scrape. This may take several minutes. Continue?'),
            header: appT('Confirm Nightly Run'),
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                setRunningNightly(true);
                try {
                    const res = await dispatch(handlePostRequest({}, endPoints.parlevelRunNightly, true, true));
                    if (res?.status === 200 || res?.status === 201) {
                        toast.success(appT('Nightly run started'));
                        setTimeout(() => { fetchStatus(); fetchLogs(); }, 5000);
                    }
                } catch {
                    toast.error(appT('Nightly run failed'));
                }
                setRunningNightly(false);
            }
        });
    };

    const handleRunChangeLevels = () => {
        confirmDialog({
            message: appT('This will retrieve current coin tube levels for connected Parlevel machines. Continue?'),
            header: appT('Confirm Change Level Run'),
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                setRunningChangeLevels(true);
                try {
                    const res = await dispatch(handlePostRequest({}, endPoints.parlevelRunChangeLevels, true, true));
                    if (res?.status === 200 || res?.status === 201) {
                        toast.success(appT('Change levels updated'));
                        fetchStatus();
                        fetchLogs();
                        const changeLevelIndex = DATA_TABS.findIndex((tab) => tab.key === 'change_levels');
                        if (activeDataTab === changeLevelIndex) fetchDataTab(changeLevelIndex);
                    }
                } catch {
                    toast.error(appT('Change level run failed'));
                }
                setRunningChangeLevels(false);
            }
        });
    };

    // ── Backfill ────────────────────────────────────────
    const formatDateForApi = (date) => {
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${m}/${d}/${date.getFullYear()}`;
    };

    const getSelectedScrapers = () => Object.entries(backfillScrapers).filter(([, v]) => v).map(([k]) => k);

    const handleRunBackfill = () => {
        const selected = getSelectedScrapers();
        if (selected.length === 0) {
            toast.warn(appT('Select at least one scraper'));
            return;
        }

        let fromDate, toDate;
        if (backfillMode === 'single') {
            if (!backfillSingleDate) { toast.warn(appT('Select a date')); return; }
            fromDate = formatDateForApi(backfillSingleDate);
            toDate = fromDate;
        } else {
            if (!backfillFromDate || !backfillToDate) { toast.warn(appT('Select from and to dates')); return; }
            fromDate = formatDateForApi(backfillFromDate);
            toDate = formatDateForApi(backfillToDate);
        }

        const dateLabel = fromDate === toDate ? fromDate : `${fromDate} - ${toDate}`;

        confirmDialog({
            message: `${appT('This will backfill data for')} ${dateLabel} (${selected.length} ${appT('scrapers')}). ${appT('This runs in the background. Continue?')}`,
            header: appT('Confirm Backfill'),
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                setRunningBackfill(true);
                try {
                    const res = await dispatch(handlePostRequest(
                        { fromDate, toDate, scrapers: selected },
                        endPoints.parlevelBackfill,
                        true,
                        true
                    ));
                    if (res?.status === 200 || res?.status === 201) {
                        toast.success(appT('Backfill started. Check scrape logs for progress.'));
                        setTimeout(() => { fetchStatus(); fetchLogs(); }, 5000);
                    }
                } catch {
                    toast.error(appT('Backfill request failed'));
                }
                setRunningBackfill(false);
            }
        });
    };

    const buildRecipientPayload = (emailList) => {
        return (Array.isArray(emailList) ? emailList : [])
            .map((email) => recipientOptions.find((option) => option.email === email))
            .filter(Boolean)
            .map((item) => ({
                user_id: item.user_id || null,
                email: item.email,
                name: item.name || item.email
            }));
    };

    const handleSaveRecipients = async () => {
        setSavingReportRecipients(true);
        try {
            const payload = {
                recipients: buildRecipientPayload(legacyRecipients),
                scheduled_recipients: {
                    daily: buildRecipientPayload(scheduledRecipients.daily),
                    weekly: buildRecipientPayload(scheduledRecipients.weekly),
                    monthly: buildRecipientPayload(scheduledRecipients.monthly)
                }
            };
            const res = await dispatch(handlePutRequest(payload, endPoints.parlevelReportRecipients, true, false));
            if (res?.status === 200 || res?.status === 201) {
                toast.success(appT('Report recipients saved'));
                fetchReportRecipients();
            }
        } catch {
            toast.error(appT('Failed to save report recipients'));
        }
        setSavingReportRecipients(false);
    };

    const handleTriggerReport = async (reportType) => {
        setTriggeringReportType(reportType);
        try {
            const selectedForTest = Array.isArray(testRecipients) ? testRecipients.filter(Boolean) : [];
            if (selectedForTest.length === 0) {
                toast.warn(appT('Select at least one recipient for test email'));
                setTriggeringReportType('');
                return;
            }
            const res = await dispatch(
                handlePostRequest({ reportType, recipients: selectedForTest }, endPoints.parlevelTriggerReportEmail, true, false)
            );
            if (res?.status === 200 || res?.status === 201) {
                toast.success(appT(`${reportType} report triggered`));
            }
        } catch {
            toast.error(appT('Failed to trigger report email'));
        }
        setTriggeringReportType('');
    };

    const SCRAPER_LABELS = {
        consolidation: 'Consolidation',
        route_sales: 'Route Sales',
        pos_sales: 'POS Sales',
        collection_stats: 'Collection Stats',
        smart_routing: 'Smart Routing',
    };

    const BACKFILL_MODE_OPTIONS = [
        { label: 'Single Date', value: 'single' },
        { label: 'Date Range', value: 'range' }
    ];

    // ── Status card helpers ─────────────────────────────
    const collectionCounts = status?.collections || {};
    const latestByScanType = status?.latest_by_scan_type || {};
    const lastMorning = latestByScanType.morning_refresh || latestByScanType.morning || null;
    const lastNightly = latestByScanType.nightly_refresh || latestByScanType.nightly || null;
    const statusHealth = status?.health || {};
    const freshnessStatus = status?.freshness || {};
    const staleCollections = Object.entries(freshnessStatus)
        .filter(([, entry]) => entry?.stale)
        .map(([key]) => COLLECTION_LABELS[key] || key);
    const nightlyMissing = Array.isArray(statusHealth.nightly_missing_sources)
        ? statusHealth.nightly_missing_sources
        : [];
    const nightlyFailed = Array.isArray(statusHealth.nightly_failed_sources)
        ? statusHealth.nightly_failed_sources
        : [];
    const hasNightlyIssues = statusHealth.latest_nightly_timestamp
        ? (!statusHealth.nightly_complete || nightlyMissing.length > 0 || nightlyFailed.length > 0)
        : true;
    const healthLevel = staleCollections.length > 0 || hasNightlyIssues ? 'warning' : 'ok';

    const summarizeLogSources = (sources) => {
        if (!sources || typeof sources !== 'object') return { attempted: 0, succeeded: 0, failed: 0, totalRecords: 0, errors: [] };
        let attempted = 0, succeeded = 0, failed = 0, totalRecords = 0;
        const errors = [];
        Object.entries(sources).forEach(([name, src]) => {
            if (!src || typeof src !== 'object') return;
            if (src.attempted) {
                attempted++;
                if (src.error) { failed++; errors.push(`${name}: ${src.error}`); }
                else { succeeded++; }
                totalRecords += src.count || 0;
            }
        });
        return { attempted, succeeded, failed, totalRecords, errors };
    };

    const getLogSeverity = (sources) => {
        const { attempted, failed } = summarizeLogSources(sources);
        if (attempted === 0) return 'info';
        if (failed === 0) return 'success';
        if (failed < attempted) return 'warning';
        return 'danger';
    };

    const getLogLabel = (sources) => {
        const { attempted, failed } = summarizeLogSources(sources);
        if (attempted === 0) return 'unknown';
        if (failed === 0) return 'success';
        if (failed < attempted) return 'partial';
        return 'failed';
    };

    // ── Dynamic columns for data explorer ───────────────
    const buildColumns = (rows) => {
        if (!rows || rows.length === 0) return [];
        const sample = rows[0];
        const skip = new Set(['__v']);
        return Object.keys(sample)
            .filter((k) => !skip.has(k))
            .map((key) => {
                const val = sample[key];
                const isDate = typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val);
                const isId = key === '_id';
                const isMoney = ['total_value', 'current_total_value'].includes(key);
                const isTubes = key === 'tubes';
                return {
                    field: key,
                    header: key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
                    body: isDate
                        ? (row) => formatDateTime(row[key])
                        : isId
                            ? (row) => String(row[key]).slice(-8)
                            : isMoney
                                ? (row) => `$${Number(row[key] || 0).toFixed(2)}`
                                : isTubes
                                    ? (row) => (Array.isArray(row[key]) && row[key].length
                                        ? row[key].map((tube) => `${tube.tube}: ${tube.count} × $${Number(tube.denomination || 0).toFixed(2)} = $${Number(tube.total || 0).toFixed(2)}`).join(' | ')
                                        : '-')
                                    : undefined,
                    style: isId
                        ? { maxWidth: '80px', fontFamily: 'monospace', fontSize: '0.85em' }
                        : isTubes
                            ? { minWidth: '420px', whiteSpace: 'normal' }
                            : undefined
                };
            });
    };

    const dataColumns = buildColumns(dataRows);

    const limitOptions = [
        { label: '25', value: 25 },
        { label: '50', value: 50 },
        { label: '100', value: 100 },
        { label: '200', value: 200 },
        { label: '500', value: 500 }
    ];

    return (
        <div className="grid">
            <ConfirmDialog />

            {/* ── Header ─────────────────────────────── */}
            <div className="col-12 flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                    <h2 style={{ margin: 0 }}>
                        <i className="pi pi-database mr-2" />
                        {appT('Data & Sync')}
                    </h2>
                    <p className="text-color-secondary mt-1 mb-0">{appT('Parlevel data ingestion and monitoring')}</p>
                </div>
                <div className="flex gap-2">
                    <Button label={appT('Overview')} icon="pi pi-chart-line" className="p-button-outlined p-button-sm" onClick={() => navigate('/parlevel')} />
                    <Button label={appT('Coin Tubes')} icon="pi pi-dollar" className="p-button-outlined p-button-sm" onClick={() => navigate('/parlevel/change-levels')} />
                </div>
            </div>

            {/* ── Status Cards ───────────────────────── */}
            <div className="col-12 md:col-6 lg:col-4">
                <Card title={appT('Last Morning Run')} className="h-full">
                    {statusLoading ? (
                        <i className="pi pi-spin pi-spinner" />
                    ) : lastMorning ? (
                        <div>
                            <div className="mb-2">
                                <Tag value={getLogLabel(lastMorning.sources)} severity={getLogSeverity(lastMorning.sources)} />
                            </div>
                            <div className="text-sm text-color-secondary">{formatDateTime(lastMorning.timestamp)}</div>
                            {lastMorning.duration_seconds > 0 && (
                                <div className="text-sm text-color-secondary mt-1">{lastMorning.duration_seconds}s</div>
                            )}
                        </div>
                    ) : (
                        <span className="text-color-secondary">{appT('No data')}</span>
                    )}
                </Card>
            </div>

            <div className="col-12 md:col-6 lg:col-4">
                <Card title={appT('Last Nightly Run')} className="h-full">
                    {statusLoading ? (
                        <i className="pi pi-spin pi-spinner" />
                    ) : lastNightly ? (
                        <div>
                            <div className="mb-2">
                                <Tag value={getLogLabel(lastNightly.sources)} severity={getLogSeverity(lastNightly.sources)} />
                            </div>
                            <div className="text-sm text-color-secondary">{formatDateTime(lastNightly.timestamp)}</div>
                            {lastNightly.duration_seconds > 0 && (
                                <div className="text-sm text-color-secondary mt-1">{lastNightly.duration_seconds}s</div>
                            )}
                        </div>
                    ) : (
                        <span className="text-color-secondary">{appT('No data')}</span>
                    )}
                </Card>
            </div>

            <div className="col-12 md:col-12 lg:col-4">
                <Card title={appT('Collection Counts')} className="h-full">
                    {statusLoading ? (
                        <i className="pi pi-spin pi-spinner" />
                    ) : Object.keys(collectionCounts).length > 0 ? (
                        <div className="grid">
                            {Object.entries(collectionCounts).map(([key, count]) => (
                                <div key={key} className="col-6 flex align-items-center justify-content-between py-1">
                                    <span className="text-sm">{COLLECTION_LABELS[key] || key}</span>
                                    <Tag value={String(count)} severity="info" className="ml-2" />
                                </div>
                            ))}
                            {statusHealth.latest_nightly_timestamp && (
                                <div className="col-12 pt-2">
                                    <Tag
                                        value={
                                            statusHealth.nightly_complete
                                                ? appT('Nightly complete')
                                                : appT('Nightly incomplete')
                                        }
                                        severity={statusHealth.nightly_complete ? 'success' : 'danger'}
                                    />
                                </div>
                            )}
                            {!statusHealth.nightly_complete && Array.isArray(statusHealth.nightly_missing_sources) && statusHealth.nightly_missing_sources.length > 0 && (
                                <div className="col-12 text-xs text-color-secondary mt-2">
                                    {appT('Missing:')} {statusHealth.nightly_missing_sources.join(', ')}
                                </div>
                            )}
                            {Object.values(freshnessStatus).some((entry) => entry?.stale) && (
                                <div className="col-12 text-xs text-color-secondary mt-2">
                                    {appT('Some datasets are stale (>30h).')}
                                </div>
                            )}
                        </div>
                    ) : (
                        <span className="text-color-secondary">{appT('No data')}</span>
                    )}
                </Card>
            </div>

            <div className="col-12">
                <Card title={appT('Health Monitor')}>
                    <div className="flex align-items-center gap-2 mb-3">
                        <span className="font-semibold">{appT('Overall')}:</span>
                        <Tag
                            value={healthLevel === 'ok' ? appT('OK') : appT('Warning')}
                            severity={healthLevel === 'ok' ? 'success' : 'warning'}
                        />
                    </div>
                    <div className="text-sm mb-2">
                        <span className="font-semibold">{appT('Nightly')}:</span>{' '}
                        {statusHealth.latest_nightly_timestamp
                            ? formatDateTime(statusHealth.latest_nightly_timestamp)
                            : appT('No data')}
                    </div>
                    {nightlyMissing.length > 0 && (
                        <div className="text-sm mb-2">
                            <span className="font-semibold">{appT('Missing')}:</span> {nightlyMissing.join(', ')}
                        </div>
                    )}
                    {nightlyFailed.length > 0 && (
                        <div className="text-sm mb-2">
                            <span className="font-semibold">{appT('Failed')}:</span>{' '}
                            {nightlyFailed.map((item) => `${item.source}`).join(', ')}
                        </div>
                    )}
                    <div className="text-sm">
                        <span className="font-semibold">{appT('Stale datasets')}:</span>{' '}
                        {staleCollections.length > 0 ? staleCollections.join(', ') : appT('None')}
                    </div>
                </Card>
            </div>

            {/* ── Manual Run Controls ────────────────── */}
            <div className="col-12">
                <Card title={appT('Manual Run')}>
                    <div className="flex gap-3 flex-wrap">
                        <Button
                            label={appT('Run Morning Scrape')}
                            icon="pi pi-sun"
                            loading={runningMorning}
                            onClick={handleRunMorning}
                            className="p-button-warning"
                        />
                        <Button
                            label={appT('Run Nightly Scrape')}
                            icon="pi pi-moon"
                            loading={runningNightly}
                            onClick={handleRunNightly}
                            className="p-button-help"
                        />
                        <Button
                            label={appT('Update Change Levels')}
                            icon="pi pi-dollar"
                            loading={runningChangeLevels}
                            onClick={handleRunChangeLevels}
                            className="p-button-info"
                        />
                        <Button
                            label={appT('Refresh Status')}
                            icon="pi pi-refresh"
                            loading={statusLoading}
                            onClick={() => { fetchStatus(); fetchLogs(); }}
                            className="p-button-secondary"
                        />
                    </div>
                </Card>
            </div>

            {/* ── Backfill ───────────────────────────── */}
            <div className="col-12">
                <Card title={appT('Backfill Data')}>
                    <p className="text-color-secondary text-sm mt-0 mb-3">
                        {appT('Re-scrape data from Parlevel for specific dates. Useful for fixing incorrect data or filling gaps.')}
                    </p>

                    <div className="flex flex-column gap-3">
                        {/* Mode toggle */}
                        <div className="flex align-items-center gap-3">
                            <label className="font-semibold text-sm">{appT('Mode')}:</label>
                            <SelectButton
                                value={backfillMode}
                                options={BACKFILL_MODE_OPTIONS}
                                onChange={(e) => e.value && setBackfillMode(e.value)}
                            />
                        </div>

                        {/* Date selection */}
                        <div className="flex align-items-center gap-3 flex-wrap">
                            {backfillMode === 'single' ? (
                                <>
                                    <label className="font-semibold text-sm">{appT('Date')}:</label>
                                    <Calendar
                                        value={backfillSingleDate}
                                        onChange={(e) => setBackfillSingleDate(e.value)}
                                        dateFormat="mm/dd/yy"
                                        showIcon
                                        maxDate={new Date()}
                                        className="w-12rem"
                                        placeholder="MM/DD/YYYY"
                                    />
                                </>
                            ) : (
                                <>
                                    <label className="font-semibold text-sm">{appT('From')}:</label>
                                    <Calendar
                                        value={backfillFromDate}
                                        onChange={(e) => setBackfillFromDate(e.value)}
                                        dateFormat="mm/dd/yy"
                                        showIcon
                                        maxDate={backfillToDate || new Date()}
                                        className="w-12rem"
                                        placeholder="MM/DD/YYYY"
                                    />
                                    <label className="font-semibold text-sm">{appT('To')}:</label>
                                    <Calendar
                                        value={backfillToDate}
                                        onChange={(e) => setBackfillToDate(e.value)}
                                        dateFormat="mm/dd/yy"
                                        showIcon
                                        minDate={backfillFromDate || undefined}
                                        maxDate={new Date()}
                                        className="w-12rem"
                                        placeholder="MM/DD/YYYY"
                                    />
                                </>
                            )}
                        </div>

                        {/* Scraper selection */}
                        <div>
                            <label className="font-semibold text-sm block mb-2">{appT('Scrapers')}:</label>
                            <div className="flex gap-3 flex-wrap">
                                {Object.entries(SCRAPER_LABELS).map(([key, label]) => (
                                    <div key={key} className="flex align-items-center gap-2">
                                        <Checkbox
                                            inputId={`bf-${key}`}
                                            checked={backfillScrapers[key]}
                                            onChange={(e) => setBackfillScrapers((prev) => ({ ...prev, [key]: e.checked }))}
                                        />
                                        <label htmlFor={`bf-${key}`} className="text-sm">{appT(label)}</label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Run button */}
                        <div>
                            <Button
                                label={appT('Run Backfill')}
                                icon="pi pi-history"
                                loading={runningBackfill}
                                onClick={handleRunBackfill}
                                className="p-button-info"
                            />
                        </div>
                    </div>
                </Card>
            </div>

            {/* ── Report Emails ─────────────────────── */}
            <div className="col-12">
                <Card title={appT('Report Emails')}>
                    <div className="grid">
                        <div className="col-12 lg:col-8">
                            <label className="font-semibold text-sm block mb-2">{appT('Destinatarios (respaldo general)')}</label>
                            <MultiSelect
                                value={legacyRecipients}
                                options={recipientOptions}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => setLegacyRecipients(e.value || [])}
                                placeholder={appT('Select users')}
                                className="w-full"
                                filter
                                loading={reportRecipientsLoading}
                                display="chip"
                            />
                            <small className="text-color-secondary">
                                {appT('Se usa solo como respaldo si no hay lista configurada por frecuencia.')}
                            </small>
                        </div>
                        <div className="col-12 lg:col-4 flex align-items-end">
                            <Button
                                label={appT('Save Recipients')}
                                icon="pi pi-save"
                                className="w-full p-button-primary"
                                onClick={handleSaveRecipients}
                                loading={savingReportRecipients}
                            />
                        </div>
                        <div className="col-12">
                            <div className="grid">
                                <div className="col-12 md:col-4">
                                    <label className="font-semibold text-sm block mb-2">{appT('Programado diario')}</label>
                                    <MultiSelect
                                        value={scheduledRecipients.daily}
                                        options={recipientOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => setScheduledRecipients((prev) => ({ ...prev, daily: e.value || [] }))}
                                        placeholder={appT('Select users')}
                                        className="w-full"
                                        filter
                                        loading={reportRecipientsLoading}
                                        display="chip"
                                    />
                                </div>
                                <div className="col-12 md:col-4">
                                    <label className="font-semibold text-sm block mb-2">{appT('Programado semanal')}</label>
                                    <MultiSelect
                                        value={scheduledRecipients.weekly}
                                        options={recipientOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => setScheduledRecipients((prev) => ({ ...prev, weekly: e.value || [] }))}
                                        placeholder={appT('Select users')}
                                        className="w-full"
                                        filter
                                        loading={reportRecipientsLoading}
                                        display="chip"
                                    />
                                </div>
                                <div className="col-12 md:col-4">
                                    <label className="font-semibold text-sm block mb-2">{appT('Programado mensual')}</label>
                                    <MultiSelect
                                        value={scheduledRecipients.monthly}
                                        options={recipientOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => setScheduledRecipients((prev) => ({ ...prev, monthly: e.value || [] }))}
                                        placeholder={appT('Select users')}
                                        className="w-full"
                                        filter
                                        loading={reportRecipientsLoading}
                                        display="chip"
                                    />
                                </div>
                            </div>
                            <small className="text-color-secondary">
                                {appT('Configure claramente quién recibe cada frecuencia automática: diario, semanal y mensual.')}
                            </small>
                        </div>
                    </div>
                    <div className="mt-4 p-3 border-1 border-round border-300">
                        <div className="grid">
                            <div className="col-12 md:col-4">
                                <label className="font-semibold text-sm block mb-2">{appT('Tipo de prueba')}</label>
                                <Dropdown
                                    value={testReportType}
                                    options={[
                                        { label: appT('Daily'), value: 'daily' },
                                        { label: appT('Weekly'), value: 'weekly' },
                                        { label: appT('Monthly'), value: 'monthly' },
                                    ]}
                                    onChange={(e) => setTestReportType(e.value || 'daily')}
                                    className="w-full"
                                />
                            </div>
                            <div className="col-12 md:col-8">
                                <label className="font-semibold text-sm block mb-2">{appT('Destinatarios de prueba')}</label>
                                <MultiSelect
                                    value={testRecipients}
                                    options={recipientOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setTestRecipients(e.value || [])}
                                    placeholder={appT('Select users')}
                                    className="w-full"
                                    filter
                                    loading={reportRecipientsLoading}
                                    display="chip"
                                />
                            </div>
                        </div>
                        <small className="text-color-secondary block mb-2">
                            {appT('Este envío manual usa solo los destinatarios seleccionados aquí (ideal para pruebas).')}
                        </small>
                        <div className="flex gap-2 flex-wrap">
                            <Button
                                label={appT('Send Test Email')}
                                icon="pi pi-send"
                                className="p-button-sm p-button-outlined"
                                onClick={() => handleTriggerReport(testReportType)}
                                loading={triggeringReportType === testReportType}
                            />
                        </div>
                    </div>
                </Card>
            </div>

            {/* ── Scrape Logs ────────────────────────── */}
            <div className="col-12">
                <Card title={appT('Recent Scrape Logs')}>
                    <DataTable
                        value={logs}
                        loading={logsLoading}
                        paginator
                        rows={10}
                        emptyMessage={appT('No logs found')}
                        sortField="timestamp"
                        sortOrder={-1}
                        responsiveLayout="scroll"
                    >
                        <Column
                            field="scan_type"
                            header={appT('Task')}
                            sortable
                            body={(row) => <span className="font-semibold">{row.scan_type || '-'}</span>}
                        />
                        <Column
                            field="sources"
                            header={appT('Status')}
                            body={(row) => <Tag value={getLogLabel(row.sources)} severity={getLogSeverity(row.sources)} />}
                        />
                        <Column field="timestamp" header={appT('Date')} sortable body={(row) => formatDateTime(row.timestamp)} />
                        <Column
                            field="duration_seconds"
                            header={appT('Duration')}
                            sortable
                            body={(row) => (row.duration_seconds != null ? `${row.duration_seconds}s` : '-')}
                        />
                        <Column
                            header={appT('Records')}
                            body={(row) => {
                                const { totalRecords } = summarizeLogSources(row.sources);
                                return totalRecords > 0 ? totalRecords : '-';
                            }}
                        />
                        <Column
                            header={appT('Sources')}
                            body={(row) => {
                                const { attempted, succeeded, failed } = summarizeLogSources(row.sources);
                                return `${succeeded}/${attempted}${failed > 0 ? ` (${failed} err)` : ''}`;
                            }}
                        />
                        <Column
                            header={appT('Errors')}
                            body={(row) => {
                                const { errors } = summarizeLogSources(row.sources);
                                if (errors.length === 0) return '-';
                                const text = errors.join('; ');
                                return (
                                    <span className="text-red-500 text-sm" title={text}>
                                        {text.substring(0, 80)}{text.length > 80 ? '...' : ''}
                                    </span>
                                );
                            }}
                        />
                    </DataTable>
                </Card>
            </div>

            {/* ── Data Explorer ──────────────────────── */}
            <div className="col-12">
                <Card title={appT('Data Explorer')}>
                    <div className="flex align-items-center gap-2 mb-3">
                        <label className="text-sm font-semibold">{appT('Rows')}:</label>
                        <Dropdown
                            value={dataLimit}
                            options={limitOptions}
                            onChange={(e) => setDataLimit(e.value)}
                            className="w-6rem"
                        />
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-text p-button-sm"
                            onClick={() => fetchDataTab(activeDataTab)}
                            loading={dataLoading}
                            tooltip={appT('Refresh')}
                        />
                    </div>
                    <TabView activeIndex={activeDataTab} onTabChange={(e) => setActiveDataTab(e.index)}>
                        {DATA_TABS.map((tab) => (
                            <TabPanel key={tab.key} header={appT(tab.label)}>
                                <DataTable
                                    value={dataRows}
                                    loading={dataLoading}
                                    paginator
                                    rows={10}
                                    rowsPerPageOptions={[10, 25, 50]}
                                    emptyMessage={appT('No data found')}
                                    responsiveLayout="scroll"
                                    scrollable
                                    scrollHeight="500px"
                                    size="small"
                                >
                                    {dataColumns.map((col) => (
                                        <Column
                                            key={col.field}
                                            field={col.field}
                                            header={col.header}
                                            sortable
                                            body={col.body}
                                            style={col.style}
                                        />
                                    ))}
                                </DataTable>
                            </TabPanel>
                        ))}
                    </TabView>
                </Card>
            </div>
        </div>
    );
};

export default ParlevelData;
